import { Either } from "fp-ts/lib/Either";
import MasterItemEntity from "../entities/master_item.entity";
import ErrorEntity from "../entities/error.entity";
import QACommentEntity from "../entities/qa_comment.entity";
import ClientAnalysisEntity from "../entities/client_analysis.entity";
import SearchResultEntity from "../entities/search_result.entity";

export interface AverageCalls {
    calls: number,
    agent: number,
    customer: number,
    total: number,
};


export interface Sentiments {
    negative: number,
    neutral: number,
    positive: number,
}

export interface SentimentsOnDate {
    date: Date,
    sentiments: Sentiments
}

export interface Target {
    serviceLevel: number,
    current: number,
    increment: number,
}

export interface ReasonForCalls {
    name: string,
    count: number,
}

export interface HumanRanking {
    id: string,
    name: string,
    totalEvaluatedCalls: number;
    sentiments: Sentiments,
    score: number,
    rankingPosition: number;
}

export interface GetOperationReportResponse {
    dayToDay?: SentimentsOnDate[],
    totalSentiments?: Sentiments,
    averages?: {
        completed: AverageCalls,
        failed: AverageCalls,
        notRated: AverageCalls,
    },
    totalCalls?: number,
    target?: Target
    reasonForCalls?: ReasonForCalls[],
    bestClients?: HumanRanking[],
    worstClients?: HumanRanking[],
    avgScorePerWeek?: { week: string, score: number }[],
    avgScorePerQuestion?: { question: string, score: number }[],
    avgScorePerCategory?: { category: string, score: number }[],
    avgScorePerTypeCall?: { type: string, score: number }[],
}

export interface DashboardSearchProps {
    startDate: Date, endDate: Date,
    operationId?: string, humanAgentId?: string, aiAgentId?: string, callTypeId?: string,
}

export default interface DashboardRepository {
    getClientsList: () => Promise<Either<ErrorEntity, MasterItemEntity[]>>;
    getCallAverages: (_: DashboardSearchProps) => Promise<Either<ErrorEntity, {
        completed: AverageCalls,
        failed: AverageCalls,
        notRated: AverageCalls,
        totalCalls?: number,
    }>>;
    getSentiments: (_: DashboardSearchProps) => Promise<Either<ErrorEntity, SentimentsOnDate[]>>;
    getReasonForCalls: (_: DashboardSearchProps) => Promise<Either<ErrorEntity, ReasonForCalls[]>>;
    getScoreDetails: (_: DashboardSearchProps) => Promise<Either<ErrorEntity, Target>>;
    getClientDetails: (_: { agentId: string, startDate: Date, endDate: Date }) => Promise<Either<ErrorEntity, ClientAnalysisEntity>>;
    addComment: (_: { employeeID: string, comment: QACommentEntity }) => Promise<Either<ErrorEntity, QACommentEntity>>;
    searchRanking(_: {
        query: string,
        initDate?: Date, endDate?: Date, page: number, itemsPerPage: number, sortBy: "ASC" | "DESC",
    }): Promise<Either<ErrorEntity, SearchResultEntity<HumanRanking>>>;
    
    getAvgScorePerWeek(_: DashboardSearchProps): Promise<Either<ErrorEntity, { week: string, score: number }[]>>;
    getAvgScorePerQuestion(_: DashboardSearchProps): Promise<Either<ErrorEntity, { question: string, score: number }[]>>;
    getAvgScorePerCategory(_: DashboardSearchProps): Promise<Either<ErrorEntity, { category: string, score: number }[]>>;
    getAvgScorePerTypeCall(_: DashboardSearchProps): Promise<Either<ErrorEntity, { type: string, score: number }[]>>;

    getTopAndBottomAgents(_: DashboardSearchProps): Promise<Either<ErrorEntity, { best: HumanRanking[], worst: HumanRanking[] }>>;

    getAgentComments(_: { agentId: string, startDate: Date, endDate: Date }): Promise<Either<ErrorEntity, QACommentEntity[]>>;
}

export const DashboardRepositoryName = "DashboardRepository";